import {FormControl, TextInput} from '@primer/react'
import {memo, useEffect, useMemo, useRef, useState} from 'react'

import {TraceDetails} from './TraceDetails'
import styles from './TraceVisualizer.module.css'
import {
  assignRowsToTree,
  type DecoratedNode,
  filterTreeByVerbosity,
  fixTimes,
  hasLLMTracerData,
} from './utilities/tree-utilities'

const ROW_HEIGHT = 28
const ROW_SPACING = 2

interface TraceSettings {
  minimumDuration: number
}

function TraceSettingsControls({
  settings,
  setSettings,
}: {
  settings: TraceSettings
  setSettings: (settings: TraceSettings) => void
}) {
  return (
    <div className={styles.settings}>
      <FormControl>
        <FormControl.Label>Minimum Duration:</FormControl.Label>
        <TextInput
          type="number"
          value={settings.minimumDuration}
          onChange={e => setSettings({...settings, minimumDuration: Number(e.target.value)})}
        />
      </FormControl>
    </div>
  )
}

export function TraceVisualizer({
  root,
  verboseMode,
}: {
  root: {node: DecoratedNode; minTime: number; maxTime: number; maxDepth: number}
  verboseMode: boolean
}) {
  const [settings, setSettings] = useState<TraceSettings>({
    minimumDuration: 0,
  })
  const [zoom, setZoom] = useState(1)
  const [activeNode, setActiveNode] = useState<DecoratedNode | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveNode(null)
  }, [root])

  const {
    node: decoratedRoot,
    maxRow,
    maxTime,
  } = useMemo(() => {
    const filtered = filterTreeByVerbosity(root.node, verboseMode)
    if (!filtered) return {node: null, maxRow: 0, maxTime: 0}
    let r = fixTimes(filtered, root.minTime, root.maxTime)
    r = filterNodes(r, settings.minimumDuration)

    // Assign rows to handle parallel spans stacking
    const {node: rowAssigned, maxRow: computedMaxRow} = assignRowsToTree(r)
    return {node: rowAssigned, maxRow: computedMaxRow, maxTime: root.maxTime - root.minTime}
  }, [root, verboseMode, settings.minimumDuration])

  return (
    <div className={styles.traceVisualizerContainer} ref={containerRef}>
      <TraceSettingsControls settings={settings} setSettings={setSettings} />
      {decoratedRoot ? (
        <div
          className={styles.traceVisualizer}
          style={{minHeight: `${(maxRow + 1) * (ROW_HEIGHT + ROW_SPACING)}px`, width: `${zoom * 100}%`}}
          onWheel={e => {
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault()
              const scaleFactor = Math.max(0.5, 1 + e.deltaY * -0.01)
              const visualizer = containerRef.current
              if (visualizer) {
                visualizer.scrollLeft *= scaleFactor
              }
              setZoom(prevZoom => prevZoom * scaleFactor)
            }
          }}
        >
          <Node node={decoratedRoot} maxTime={maxTime} setActiveNode={setActiveNode} activeNode={activeNode} />
        </div>
      ) : (
        <div style={{padding: '16px', textAlign: 'center', color: '#666'}}>
          No spans to display. Try enabling verbose mode to see all spans.
        </div>
      )}
      {activeNode && <TraceDetails node={activeNode} />}
    </div>
  )
}

function NodeImpl({
  node,
  maxTime,
  setActiveNode,
  activeNode,
}: {
  node: DecoratedNode
  maxTime: number
  setActiveNode: (node: DecoratedNode) => void
  activeNode: DecoratedNode | null
}) {
  const leftpctg = percentage(node.startTime, maxTime)
  const left = `${leftpctg}%`
  const width = `${percentage(node.duration, maxTime)}%`
  const top = `${node.row * (ROW_HEIGHT + ROW_SPACING)}px`
  const isActive = activeNode?.spanId === node.spanId

  const baseClassName = hasLLMTracerData(node) ? styles.nodeContainerWithData : styles.nodeContainer
  const containerClassName = isActive ? `${baseClassName} ${styles.active}` : baseClassName

  return (
    <div className={containerClassName}>
      <button className={styles.node} style={{left, width, top}} onClick={() => setActiveNode(node)}>
        <span className={styles.label}>{node.name}</span>
      </button>
      {node.children.map(child => (
        <Node key={child.spanId} node={child} maxTime={maxTime} setActiveNode={setActiveNode} activeNode={activeNode} />
      ))}
    </div>
  )
}

const Node = memo(NodeImpl)

function percentage(value: number, max: number): number {
  return (value / max) * 100
}

function filterNodes(root: DecoratedNode, minimumDuration: number): DecoratedNode {
  // we will never filter out the root node — return a new object to avoid mutating the input
  return {
    ...root,
    children: root.children.map(child => filterNodesRecurse(child, minimumDuration)).flat(),
  }
}

function filterNodesRecurse(node: DecoratedNode, minimumDuration: number): DecoratedNode[] {
  const filteredChildren = node.children.map(child => filterNodesRecurse(child, minimumDuration)).flat()
  if (node.duration < minimumDuration) {
    // this isn't likely to happen with the current filtering because the children will also be too small, but when we offer more filtering options, this will be useful
    return filteredChildren // the children become children of the parent node
  } else {
    return [{...node, children: filteredChildren}]
  }
}
